import { ReactNode } from 'react';
import { array, nonEmptyArray, boolean, ord, number } from 'fp-ts';
import { identity, pipe } from 'fp-ts/function';
import {
    NewRoutingRuleAssignment,
    NewRoutingRuleResult,
    ProductCategory,
    RoutingRuleProductAssignment,
    RoutingRuleProductCategoryAssignment,
    RoutingRuleAccountingCategoryAssignment,
    Service,
} from '@mews-commander/data-access';
import { upperFirst } from '@mews/std';
import { lt } from '@mews/framework';
import { getChildrenProductCategoryIdsFromProductCategoryId } from '@mews-commander/legacy-entities/src/utils/productCategories';
import { AdditionalService, BookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { ServiceTimeUnit } from '../../../components/commander-ui';
import { LocalizedProduct } from '../components/types';
import { getRoutingRuleAssignments } from './newRoutingRuleResult';

export interface RoutingRuleItemsFilters {
    serviceIds?: string[];
    productIds?: string[];
}

export interface RoutingRuleItemsAdditionalData {
    allServices: { additionalServices: AdditionalService[]; bookableServices: BookableService[] };
    allProducts?: LocalizedProduct[];
    allProductCategories?: ProductCategory[];
}

const getRoutingRuleRoutedItemTypesItems = (assignments: NewRoutingRuleAssignment[]): ReactNode[] => {
    const items = [];
    const { serviceId } = assignments[0] || {};
    const cityTax = assignments.some((assignment) => assignment.routedItemTypes.cityTax);
    const spaceOrder = assignments.some((assignment) => assignment.routedItemTypes.spaceOrder);
    const allProducts = assignments.some((assignment) => assignment.routedItemTypes.allProducts);
    const deposits = assignments.some((assignment) => assignment.routedItemTypes.deposits);
    const additionalExpenses = assignments.some((assignment) => assignment.routedItemTypes.additionalExpenses);
    const allCustomItems = assignments.some((assignment) => assignment.routedItemTypes.allCustomItems);

    if (spaceOrder) {
        items.push(<ServiceTimeUnit serviceId={serviceId} />);
    }

    if (cityTax) {
        items.push(lt('CityTax'));
    }

    if (deposits) {
        items.push(lt('DepositPlural'));
    }

    if (additionalExpenses) {
        items.push(lt('AdditionalExpensePlural'));
    }

    if (allProducts) {
        items.push(
            <>
                {lt('Products')} ({lt('All')})
            </>
        );
    }

    if (allCustomItems) {
        items.push(
            <>
                {lt('CustomItemPlural')} ({lt('All')})
            </>
        );
    }

    return items;
};

export const getProductCategoryAssignmentItems = (
    productCategoryAssignments: RoutingRuleProductCategoryAssignment[],
    routingRuleAssignmentIds: string[],
    allProducts: LocalizedProduct[],
    productCategories: ProductCategory[],
    aggregated?: boolean
): ReactNode[] => {
    const productCategoryIds = productCategoryAssignments
        .filter((v) => routingRuleAssignmentIds.includes(v.routingRuleAssignmentId))
        .flatMap((productCategoryAssignment) => [
            productCategoryAssignment.productCategoryId,
            ...getChildrenProductCategoryIdsFromProductCategoryId(
                productCategoryAssignment.productCategoryId,
                productCategories
            ),
        ]);

    if (aggregated) {
        return productCategoryIds.length
            ? [
                  <>
                      {lt('ProductCategoryPlural')} ({productCategoryIds.length})
                  </>,
              ]
            : [];
    }

    return allProducts
        .filter((product) => product.productCategoryId && productCategoryIds.includes(product.productCategoryId))
        .map((p) => upperFirst(p.localizedName.toLocaleLowerCase()));
};

const getProductAssignmentItems = (
    productAssignments: RoutingRuleProductAssignment[],
    routingRuleAssignmentIds: string[],
    productIds?: string[],
    aggregated?: boolean
): ReactNode[] => {
    const products = productAssignments
        .filter(
            (v) =>
                routingRuleAssignmentIds.includes(v.routingRuleAssignmentId) &&
                (!productIds?.length || productIds.includes(v.productId))
        )
        .map((v) => v.productName);

    if (aggregated) {
        return products.length
            ? [
                  <>
                      {lt('Products')} ({products.length})
                  </>,
              ]
            : [];
    }

    return products;
};

const getAccountingCategoryAssignmentItems = (
    accountingCategoryAssignments: RoutingRuleAccountingCategoryAssignment[],
    routingRuleAssignmentIds: string[],
    aggregated?: boolean
): ReactNode[] => {
    const assignments = accountingCategoryAssignments.filter((accountingCategoryAssignment) =>
        routingRuleAssignmentIds.includes(accountingCategoryAssignment.routingRuleAssignmentId)
    );

    if (!assignments.length) {
        return [];
    }

    const productsAccountingCategories = assignments.filter((assignment) => assignment.itemTypes.products);
    const customItemsAccountingCategories = assignments.filter((assignment) => assignment.itemTypes.customItems);
    const hasProductsAndCustomItemsAccountingCategories =
        productsAccountingCategories.length && customItemsAccountingCategories.length;

    if (aggregated) {
        if (customItemsAccountingCategories.length && !hasProductsAndCustomItemsAccountingCategories) {
            return [
                lt('RoutingRuleDetailCustomItemsAccountingCategories', {
                    count: customItemsAccountingCategories.length,
                }),
            ];
        }

        return [
            <>
                {lt('RoutingRuleDetailAccountingCategories')} ({assignments.length})
            </>,
        ];
    }

    return assignments.map((assignment) => assignment.accountingCategoryName);
};

interface GetRoutingRuleItemsParams {
    routingRuleResult: NewRoutingRuleResult;
    additionalData: RoutingRuleItemsAdditionalData;
    index: number;
    filters: RoutingRuleItemsFilters;
    aggregated?: boolean;
}

export const getRoutingRuleItems = ({
    routingRuleResult,
    additionalData: { allProducts = [], allServices, allProductCategories = [] },
    index,
    filters: { serviceIds, productIds },
    aggregated,
}: GetRoutingRuleItemsParams): Array<{ serviceName: string; assignmentId: string; items: Array<ReactNode> }> => {
    const { bookableServices, additionalServices } = allServices;
    const servicesMap = [...bookableServices, ...additionalServices].reduce<Record<string, Service>>((acc, service) => {
        acc[service.id] = service;
        return acc;
    }, {});
    const additionalServicesIds = additionalServices.map((v) => v.id);

    const routingRuleAssignments = pipe(
        getRoutingRuleAssignments(routingRuleResult, index),
        serviceIds?.length ? array.filter((v) => serviceIds.includes(v.serviceId)) : identity,
        array.sortBy([
            pipe(
                boolean.Ord,
                ord.contramap(({ serviceId }: NewRoutingRuleAssignment) => additionalServicesIds.includes(serviceId))
            ),
            pipe(
                number.Ord,
                ord.contramap(({ serviceId }: NewRoutingRuleAssignment) => servicesMap[serviceId]?.ordering || 0)
            ),
        ])
    );

    const productCategories = serviceIds?.length
        ? allProductCategories.filter((v) => serviceIds.includes(v.serviceId))
        : [];

    return pipe(
        routingRuleAssignments,
        nonEmptyArray.groupBy((v) => v.serviceId),
        Object.values,
        array.map((assignmentsPerService: NewRoutingRuleAssignment[]) => {
            const assignment = assignmentsPerService[0];
            const assignmentIds = assignmentsPerService.map((v) => v.id);

            return {
                serviceName: assignment.serviceName,
                assignmentId: assignmentIds.join(','),
                items: [
                    ...getRoutingRuleRoutedItemTypesItems(assignmentsPerService),
                    ...getProductCategoryAssignmentItems(
                        routingRuleResult.productCategoryAssignments ?? [],
                        assignmentIds,
                        allProducts,
                        productCategories,
                        aggregated
                    ),
                    ...getProductAssignmentItems(
                        routingRuleResult.productAssignments ?? [],
                        assignmentIds,
                        productIds,
                        aggregated
                    ),
                    ...getAccountingCategoryAssignmentItems(
                        routingRuleResult.accountingCategoryAssignments ?? [],
                        assignmentIds,
                        aggregated
                    ),
                ],
            };
        }),
        array.filter((v) => v.items.length > 0)
    );
};
